import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, UsersIcon } from 'lucide-react';
import { officers } from '../../data/officers';
import { Officer } from '../../types/commission';
import { roleFor } from '../../utils/officerPeriod';
import { HeaderDropdown } from '../HeaderDropdown';
interface DrilldownBarProps {
  officer: Officer;
}

/** Breadcrumb + person switcher shown above an admin drill-down. */
export function DrilldownBar({
  officer
}: DrilldownBarProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const index = officers.findIndex(item => item.id === officer.id);
  const previous = officers[(index - 1 + officers.length) % officers.length];
  const next = officers[(index + 1) % officers.length];
  const needle = search.trim().toLowerCase();
  const matches = officers.filter(item => !needle || item.name.toLowerCase().includes(needle) || item.email.toLowerCase().includes(needle) || roleFor(item).name.toLowerCase().includes(needle));
  return <div className="border-b border-slate-200 bg-slate-50" data-id="element-63" data-mp="1gvvdadskocjy">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-3" data-id="element-64" data-mp="1luf1kkun20fh">
        <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-sm" data-id="element-65" data-mp="1qklomg9i5cvm">
          <Link to="/" className="inline-flex items-center gap-1.5 font-semibold text-brand-700 transition hover:text-brand-900" data-id="element-66" data-mp="1vjyhbhn21e64">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-67" data-mp="20h87x0m6jvvj" />
            Company overview
          </Link>
          <span aria-hidden="true" className="text-slate-300" data-id="element-68" data-mp="24ik2lkjwv7al">
            /
          </span>
          <span aria-current="page" className="truncate font-semibold text-slate-700" data-id="element-69" data-mp="29hn44v0omvdt">
            {officer.name}
          </span>
        </nav>

        <div className="flex items-center gap-2" data-id="element-70" data-mp="f4wyxh4v5zr4">
          <HeaderDropdown icon={<UsersIcon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-72" data-mp="1igce8z3kzk5c" />} label="Jump to person" menuLabel="Jump to person" triggerClassName="w-48" menuClassName="w-80" data-id="element-71" data-mp="f7sy18sg6uh5">
            {close => <>
                <div className="px-2 pb-1 pt-1.5" data-id="element-73" data-mp="1mi7jgeomuhoe">
                  <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search people..." aria-label="Search people" autoFocus className="w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400" data-id="element-74" data-mp="1mgnzoe3rpohv" />
                </div>
                {matches.map(item => {
              const isSelected = item.id === officer.id;
              return <button key={item.id} type="button" role="option" aria-selected={isSelected} onClick={() => {
                navigate(`/people/${item.id}`);
                setSearch('');
                close();
              }} className={`flex w-full items-center gap-3 px-3.5 py-2 text-left text-sm transition hover:bg-slate-50 ${isSelected ? 'bg-brand-50' : ''}`} data-id="element-75" data-mp="2f8g2s9uwn84k">
                      <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-900 text-[10px] font-bold text-white" data-id="element-76" data-mp="2ku87vf4flxi4">
                        {item.initials}
                      </span>
                      <span className="min-w-0" data-id="element-77" data-mp="1x5fi60eeklvy">
                        <span className={`block ${isSelected ? 'font-semibold text-brand-800' : 'font-medium text-slate-800'}`} data-id="element-78" data-mp="21567dzoa6tyd">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-slate-500" data-id="element-79" data-mp="1p2dayz9gytg5">{roleFor(item).name}</span>
                      </span>
                    </button>;
            })}
                {matches.length === 0 ? <p className="px-3.5 py-3 text-sm text-slate-500" data-id="element-80" data-mp="1fc21yyaxhu2e">No matching people.</p> : null}
              </>}
          </HeaderDropdown>

          <div className="flex items-center rounded-lg border border-slate-300 bg-white" data-id="element-81" data-mp="16cf2wj5o90ci">
            <button type="button" onClick={() => navigate(`/people/${previous.id}`)} aria-label={`Previous person: ${previous.name}`} className="rounded-l-lg p-2 text-slate-600 transition hover:bg-slate-50 hover:text-slate-900" data-id="element-82" data-mp="11d2i03yd23ck">
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-83" data-mp="2qrz5brjikgj5" />
            </button>
            <span className="border-x border-slate-200 px-2.5 text-xs font-semibold tabular-nums text-slate-500" data-id="element-84" data-mp="2qq5us15s5c5v">
              {index + 1} of {officers.length}
            </span>
            <button type="button" onClick={() => navigate(`/people/${next.id}`)} aria-label={`Next person: ${next.name}`} className="rounded-r-lg p-2 text-slate-600 transition hover:bg-slate-50 hover:text-slate-900" data-id="element-85" data-mp="338rqme18wfwz">
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-86" data-mp="2gbpxuhypcs9b" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}